import getStateManager from '../StateManager.js';
import useCustomizationState from '../Hooks/useCustomizationState.js';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { cloneElement, createContext, useCallback, useContext, useState } from 'react';
import { IsExcludedBasedOnTypeOrProject } from '../Utils/utils.js';

const CustomizationScopeContext = createContext('');

export function getScopedCustomizationId(id, scope) {
  return scope ? `${scope}:${id}` : id;
}

export function getVisibilityIconStyle(position, anchorBounds, containerBounds) {
  const common = { position: 'absolute' };

  if (position === 'heading-end' && anchorBounds && containerBounds) {
    return {
      ...common,
      left: anchorBounds.right - containerBounds.left + 8,
      top: anchorBounds.top - containerBounds.top + Math.max(0, (anchorBounds.height - 20) / 2)
    };
  }

  if (position === 'row-gutter') {
    return { ...common, left: 8, top: '50%', transform: 'translateY(-50%)' };
  }

  return { ...common, left: 4, top: 4 };
}

export function CustomizableScope({ children, scope }) {
  return (
    <CustomizationScopeContext.Provider value={scope ?? ''}>
      {children}
    </CustomizationScopeContext.Provider>
  );
}

export function CustomizableComponent({
  id,
  ext_id = '',
  controller,
  parent,
  children,
  visibilityIconPosition = 'top-left',
  zIndex
}) {
  const store = getStateManager();
  const scope = useContext(CustomizationScopeContext);
  const scopedId = getScopedCustomizationId(id, scope);
  const { editMode, visible } = useCustomizationState(store, controller.id, parent.id, scopedId);

  const toggle = () => {
    store.toggleCustomComponentVisibility(controller, parent, scopedId);
  };

  const [containerNode, setContainerNode] = useState(null);
  const setContainerRef = useCallback((node) => setContainerNode(node), []);
  const visibilityAnchor =
    visibilityIconPosition === 'heading-end'
      ? containerNode?.querySelector('[data-customization-heading]')
      : null;
  const containerBounds = visibilityAnchor ? containerNode.getBoundingClientRect() : null;
  const anchorBounds = visibilityAnchor ? visibilityAnchor.getBoundingClientRect() : null;
  const visibilityIconStyle = getVisibilityIconStyle(
    visibilityIconPosition,
    anchorBounds,
    containerBounds
  );
  const VisibilityControl = visible ? VisibilityIcon : VisibilityOffIcon;
  const overlayStyle = containerNode
    ? {
        zIndex: 100 + Number(zIndex ?? 0),
        margin: 0,
        padding: 0,
        position: 'absolute',
        pointerEvents: visible ? 'none' : 'auto',
        top: containerNode.offsetTop,
        left: containerNode.offsetLeft,
        width: containerNode.offsetWidth,
        height: containerNode.offsetHeight,
        ...(visible
          ? {}
          : {
              backdropFilter: 'blur(1px)',
              backgroundColor: 'rgba(255,255,255,0.5)'
            })
      }
    : null;
  const iconStyle = {
    cursor: 'pointer',
    fontSize: '1.25rem',
    opacity: 0.8,
    pointerEvents: 'auto',
    ...visibilityIconStyle
  };

  const exclusionId = ext_id.startsWith('edit_') ? ext_id.slice(5) : null;
  if (
    exclusionId &&
    (IsExcludedBasedOnTypeOrProject(controller, exclusionId, 'headlines') ||
      IsExcludedBasedOnTypeOrProject(controller, exclusionId, 'values'))
  ) {
    return null;
  }
  return (
    <>
      {editMode ? (
        <>
          {cloneElement(children, {
            ref: setContainerRef,
            reference: setContainerRef,
            ...(visibilityIconPosition === 'row-gutter'
              ? { 'data-customization-position': 'row-gutter' }
              : {})
          })}

          {containerNode && (
            <div style={overlayStyle}>
              <VisibilityControl
                aria-label={`${visible ? 'hide' : 'show'} ${id}`}
                data-customization-control={scopedId}
                onClick={toggle}
                style={iconStyle}
              />
            </div>
          )}
        </>
      ) : (
        <>{visible && children}</>
      )}
    </>
  );
}
